/**
 * Touch Controls System
 * Minecraft-inspired mobile controls with virtual joystick and action buttons
 */

import { MOVE_SPEED } from '../config/controls.js';

// Touch state
const touchState = {
    // Joystick state
    joystickActive: false,
    joystickTouchId: null,
    joystickStartX: 0,
    joystickStartY: 0,
    joystickCurrentX: 0,
    joystickCurrentY: 0,
    moveX: 0,
    moveZ: 0,

    // Button states (true only on the frame they were pressed)
    jump: false,
    attack: false,
    dodge: false,

    // Button held states (for visual feedback)
    jumpHeld: false,
    attackHeld: false,
    dodgeHeld: false,

    // Track which touches are on which buttons
    buttonTouches: new Map()
};

// Configuration
const JOYSTICK_MAX_RADIUS = 50; // Maximum joystick displacement
const JOYSTICK_DEADZONE = 0.15; // Deadzone as percentage

// DOM elements (cached for performance)
let joystickContainer = null;
let joystickBase = null;
let joystickKnob = null;
let jumpButton = null;
let attackButton = null;
let dodgeButton = null;
let touchControlsContainer = null;

// Check if device supports touch
export function isTouchDevice() {
    return ('ontouchstart' in window) ||
           (navigator.maxTouchPoints > 0) ||
           (navigator.msMaxTouchPoints > 0);
}

// Initialize touch controls
export function initTouchControls() {
    // Only show on touch devices
    if (!isTouchDevice()) {
        return false;
    }

    createTouchUI();
    setupTouchListeners();

    console.log('Touch controls initialized');
    return true;
}

// Create the touch UI elements
function createTouchUI() {
    // Main container
    touchControlsContainer = document.createElement('div');
    touchControlsContainer.id = 'touch-controls';
    touchControlsContainer.innerHTML = `
        <!-- Left side: Virtual Joystick -->
        <div class="touch-joystick-area" id="joystick-area">
            <div class="touch-joystick-base" id="joystick-base">
                <div class="touch-joystick-knob" id="joystick-knob"></div>
            </div>
        </div>

        <!-- Right side: Action Buttons -->
        <div class="touch-buttons-area" id="buttons-area">
            <button class="touch-button touch-button-jump" id="touch-jump">
                <span class="touch-button-icon">^</span>
                <span class="touch-button-label">JUMP</span>
            </button>
            <button class="touch-button touch-button-attack" id="touch-attack">
                <span class="touch-button-icon">*</span>
                <span class="touch-button-label">ATTACK</span>
            </button>
            <button class="touch-button touch-button-dodge" id="touch-dodge">
                <span class="touch-button-icon">~</span>
                <span class="touch-button-label">DODGE</span>
            </button>
        </div>
    `;

    document.body.appendChild(touchControlsContainer);

    // Cache DOM elements
    joystickContainer = document.getElementById('joystick-area');
    joystickBase = document.getElementById('joystick-base');
    joystickKnob = document.getElementById('joystick-knob');
    jumpButton = document.getElementById('touch-jump');
    attackButton = document.getElementById('touch-attack');
    dodgeButton = document.getElementById('touch-dodge');

    // Add styles
    addTouchStyles();
}

// Add CSS styles for touch controls
function addTouchStyles() {
    const style = document.createElement('style');
    style.id = 'touch-controls-style';
    style.textContent = `
        /* Touch Controls Container */
        #touch-controls {
            position: fixed;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            pointer-events: none;
            z-index: 500;
            user-select: none;
            -webkit-user-select: none;
            touch-action: none;
        }

        /* Joystick Area - Left Side */
        .touch-joystick-area {
            position: absolute;
            left: 0;
            bottom: 0;
            width: 50%;
            height: 50%;
            pointer-events: auto;
            display: flex;
            align-items: flex-end;
            justify-content: flex-start;
            padding: 20px;
            box-sizing: border-box;
        }

        .touch-joystick-base {
            width: 120px;
            height: 120px;
            border-radius: 50%;
            background: rgba(255, 255, 255, 0.2);
            border: 3px solid rgba(255, 255, 255, 0.4);
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            box-shadow: 0 0 20px rgba(0, 0, 0, 0.3);
        }

        .touch-joystick-knob {
            width: 50px;
            height: 50px;
            border-radius: 50%;
            background: radial-gradient(circle at 30% 30%, #ffffff, #cccccc);
            border: 2px solid rgba(255, 255, 255, 0.8);
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.3);
            position: absolute;
            transition: none;
        }

        .touch-joystick-base.active {
            background: rgba(255, 255, 255, 0.3);
            border-color: rgba(255, 255, 255, 0.6);
        }

        .touch-joystick-base.active .touch-joystick-knob {
            background: radial-gradient(circle at 30% 30%, #4CAF50, #2E7D32);
        }

        /* Action Buttons Area - Right Side */
        .touch-buttons-area {
            position: absolute;
            right: 20px;
            bottom: 20px;
            pointer-events: auto;
            display: flex;
            flex-direction: column;
            gap: 15px;
            align-items: flex-end;
        }

        .touch-button {
            width: 80px;
            height: 80px;
            border-radius: 50%;
            border: 3px solid rgba(255, 255, 255, 0.5);
            background: rgba(0, 0, 0, 0.5);
            color: white;
            font-family: Arial, sans-serif;
            font-weight: bold;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            gap: 2px;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.4);
            transition: transform 0.1s, background 0.1s;
            cursor: pointer;
            outline: none;
            -webkit-tap-highlight-color: transparent;
        }

        .touch-button-icon {
            font-size: 24px;
            line-height: 1;
        }

        .touch-button-label {
            font-size: 10px;
            opacity: 0.8;
        }

        /* Jump button - Green */
        .touch-button-jump {
            background: rgba(76, 175, 80, 0.6);
            border-color: rgba(76, 175, 80, 0.8);
        }

        .touch-button-jump:active,
        .touch-button-jump.pressed {
            background: rgba(76, 175, 80, 0.9);
            transform: scale(0.95);
            box-shadow: 0 0 20px rgba(76, 175, 80, 0.8);
        }

        /* Attack button - Red */
        .touch-button-attack {
            background: rgba(244, 67, 54, 0.6);
            border-color: rgba(244, 67, 54, 0.8);
            width: 90px;
            height: 90px;
        }

        .touch-button-attack:active,
        .touch-button-attack.pressed {
            background: rgba(244, 67, 54, 0.9);
            transform: scale(0.95);
            box-shadow: 0 0 20px rgba(244, 67, 54, 0.8);
        }

        .touch-button-attack .touch-button-icon {
            font-size: 28px;
        }

        /* Dodge button - Blue */
        .touch-button-dodge {
            background: rgba(33, 150, 243, 0.6);
            border-color: rgba(33, 150, 243, 0.8);
        }

        .touch-button-dodge:active,
        .touch-button-dodge.pressed {
            background: rgba(33, 150, 243, 0.9);
            transform: scale(0.95);
            box-shadow: 0 0 20px rgba(33, 150, 243, 0.8);
        }

        /* Responsive adjustments for smaller screens */
        @media (max-width: 600px) {
            .touch-joystick-base {
                width: 100px;
                height: 100px;
            }

            .touch-joystick-knob {
                width: 40px;
                height: 40px;
            }

            .touch-button {
                width: 65px;
                height: 65px;
            }

            .touch-button-attack {
                width: 75px;
                height: 75px;
            }

            .touch-button-icon {
                font-size: 20px;
            }

            .touch-button-label {
                font-size: 8px;
            }

            .touch-buttons-area {
                gap: 10px;
            }
        }

        /* Landscape phone adjustments */
        @media (max-height: 500px) and (orientation: landscape) {
            .touch-joystick-area {
                height: 80%;
                padding: 10px 20px;
            }

            .touch-joystick-base {
                width: 90px;
                height: 90px;
            }

            .touch-joystick-knob {
                width: 35px;
                height: 35px;
            }

            .touch-buttons-area {
                flex-direction: row;
                bottom: 15px;
                right: 15px;
                gap: 10px;
            }

            .touch-button {
                width: 60px;
                height: 60px;
            }

            .touch-button-attack {
                width: 70px;
                height: 70px;
            }
        }

        /* Hide on non-touch desktop */
        @media (hover: hover) and (pointer: fine) {
            #touch-controls {
                display: none;
            }
        }
    `;

    document.head.appendChild(style);
}

// Setup touch event listeners
function setupTouchListeners() {
    // Joystick touch events
    joystickContainer.addEventListener('touchstart', handleJoystickStart, { passive: false });
    joystickContainer.addEventListener('touchmove', handleJoystickMove, { passive: false });
    joystickContainer.addEventListener('touchend', handleJoystickEnd, { passive: false });
    joystickContainer.addEventListener('touchcancel', handleJoystickEnd, { passive: false });

    // Button touch events
    setupButtonListeners(jumpButton, 'jump');
    setupButtonListeners(attackButton, 'attack');
    setupButtonListeners(dodgeButton, 'dodge');

    // Prevent default touch behaviors on the canvas
    const canvas = document.getElementById('application');
    if (canvas) {
        canvas.addEventListener('touchstart', (e) => e.preventDefault(), { passive: false });
        canvas.addEventListener('touchmove', (e) => e.preventDefault(), { passive: false });
    }
}

// Setup listeners for a button
function setupButtonListeners(button, action) {
    button.addEventListener('touchstart', (e) => {
        e.preventDefault();
        e.stopPropagation();

        const touch = e.changedTouches[0];
        touchState.buttonTouches.set(touch.identifier, action);

        // Set the action flag (will be consumed in getTouchInput)
        touchState[action] = true;
        touchState[action + 'Held'] = true;

        // Visual feedback
        button.classList.add('pressed');
    }, { passive: false });

    button.addEventListener('touchend', (e) => {
        e.preventDefault();
        e.stopPropagation();

        for (const touch of e.changedTouches) {
            if (touchState.buttonTouches.get(touch.identifier) === action) {
                touchState.buttonTouches.delete(touch.identifier);
                touchState[action + 'Held'] = false;
                button.classList.remove('pressed');
            }
        }
    }, { passive: false });

    button.addEventListener('touchcancel', (e) => {
        for (const touch of e.changedTouches) {
            if (touchState.buttonTouches.get(touch.identifier) === action) {
                touchState.buttonTouches.delete(touch.identifier);
                touchState[action + 'Held'] = false;
                button.classList.remove('pressed');
            }
        }
    }, { passive: false });
}

// Joystick handlers
function handleJoystickStart(e) {
    e.preventDefault();

    // Only track one joystick touch at a time
    if (touchState.joystickActive) return;

    const touch = e.changedTouches[0];
    const rect = joystickBase.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;

    touchState.joystickActive = true;
    touchState.joystickTouchId = touch.identifier;
    touchState.joystickStartX = centerX;
    touchState.joystickStartY = centerY;
    touchState.joystickCurrentX = touch.clientX;
    touchState.joystickCurrentY = touch.clientY;

    joystickBase.classList.add('active');

    updateJoystickPosition();
}

function handleJoystickMove(e) {
    e.preventDefault();

    for (const touch of e.changedTouches) {
        if (touch.identifier === touchState.joystickTouchId) {
            touchState.joystickCurrentX = touch.clientX;
            touchState.joystickCurrentY = touch.clientY;
            updateJoystickPosition();
            break;
        }
    }
}

function handleJoystickEnd(e) {
    for (const touch of e.changedTouches) {
        if (touch.identifier === touchState.joystickTouchId) {
            touchState.joystickActive = false;
            touchState.joystickTouchId = null;
            touchState.moveX = 0;
            touchState.moveZ = 0;

            joystickBase.classList.remove('active');

            // Reset knob to center
            joystickKnob.style.transform = 'translate(0px, 0px)';
            break;
        }
    }
}

// Update joystick visual position and movement values
function updateJoystickPosition() {
    let dx = touchState.joystickCurrentX - touchState.joystickStartX;
    let dy = touchState.joystickCurrentY - touchState.joystickStartY;

    // Calculate distance from center
    const distance = Math.sqrt(dx * dx + dy * dy);

    // Clamp to max radius
    if (distance > JOYSTICK_MAX_RADIUS) {
        dx = (dx / distance) * JOYSTICK_MAX_RADIUS;
        dy = (dy / distance) * JOYSTICK_MAX_RADIUS;
    }

    // Update knob visual position
    joystickKnob.style.transform = `translate(${dx}px, ${dy}px)`;

    // Calculate normalized movement (-1 to 1)
    let normalizedX = dx / JOYSTICK_MAX_RADIUS;
    let normalizedY = dy / JOYSTICK_MAX_RADIUS;

    // Apply deadzone
    if (Math.abs(normalizedX) < JOYSTICK_DEADZONE) normalizedX = 0;
    if (Math.abs(normalizedY) < JOYSTICK_DEADZONE) normalizedY = 0;

    // Convert to game movement (X is left/right, Z is forward/back)
    // Note: Y on screen = Z in game (forward/back), X is same
    touchState.moveX = normalizedX * MOVE_SPEED;
    touchState.moveZ = normalizedY * MOVE_SPEED;
}

// Get current touch input state (called each frame)
export function getTouchInput() {
    const input = {
        moveX: touchState.moveX,
        moveZ: touchState.moveZ,
        jump: touchState.jump,
        attack: touchState.attack,
        dodge: touchState.dodge
    };

    // Reset button states after reading (event-based, one-frame only)
    touchState.jump = false;
    touchState.attack = false;
    touchState.dodge = false;

    return input;
}

// Check if touch controls are currently active (user is touching something)
export function isTouchActive() {
    return touchState.joystickActive ||
           touchState.jumpHeld ||
           touchState.attackHeld ||
           touchState.dodgeHeld;
}

// Show/hide touch controls
export function setTouchControlsVisible(visible) {
    if (touchControlsContainer) {
        touchControlsContainer.style.display = visible ? 'block' : 'none';
    }
}

// Clean up touch controls
export function destroyTouchControls() {
    if (touchControlsContainer) {
        touchControlsContainer.remove();
    }

    const style = document.getElementById('touch-controls-style');
    if (style) {
        style.remove();
    }
}
